import { Datepicker } from "@/components/datepicker/Datepicker.tsx";
import { emojiDefaults } from "@/components/emoji/emoji.type.ts";
import {
  elementEmployeeNPSSettingsSchema,
  elementFileSettingsSchema,
  elementGoalsSettingsSchema,
  elementOpenAnswerSettingsSchema,
  elementScaleSettingsSchema,
  elementSelectSettingsSchema,
  elementTextSettingsSchema,
  elementWriteToFieldSettingsSchema,
} from "@/components/forms/form-editor.types.ts";
import { FormEditScaleOrSelectOptionModal } from "@/components/forms/widgets/FormEditScaleOrSelectOptionModal.tsx";
import { DeleteListItem } from "@/components/list-item/DeleteListItem.tsx";
import { OptionsBulkAddModal } from "@/components/options/OptionsBulkAddModal.tsx";
import {
  Avatar,
  Box,
  Button,
  ButtonIcon,
  Checkbox,
  ConfirmationModal,
  FontAwesomeIcon,
  Input,
  Modal,
  ModalProps,
  Radio,
  Select,
  SelectOptionItem,
  SelectValueType,
  Stack,
  Switch,
  Text,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { RichEditor } from "@/components/rich-editor/RichEditor.tsx";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import {
  ApiFormElementEdit,
  ApiFormElementSelectOptionEdit,
  ApiFormElementWriteToFieldTableMode,
  ApiFormElementWriteToFieldTargetType,
} from "@/types/forms/form-edit.types.ts";
import { useAllTables, useTableFields } from "@/queries/use-custom-fields.query.ts";
import { useAppConfig } from "@/queries/settings/use-app.query.ts";
import { ApiField, ApiFieldTypes, ApiTable } from "@/types/custom-field.types.ts";
import { ApiFormElementType } from "@/types/forms/form.types";
import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import useCustomFieldsHook from "@/hooks/use-custom-fields.hook.tsx";

export interface EditQuestionModalProps extends ModalProps {
  element: ApiFormElementEdit;
  onSave: (updatedElement: ApiFormElementEdit) => Promise<void>;
  onDelete: () => Promise<void>;
}

type FormData = {
  title: string;
  description: string | null;
  is_required?: boolean;
  is_comment_enabled?: boolean;
  is_single_rating?: boolean;
  show_when_no_goals?: boolean;
  text?: string | null;
  is_multiple?: boolean;
  is_other?: boolean;
  form_scale_id?: number;
  options?: ApiFormElementSelectOptionEdit[];
  date_starts_at?: Date;
  date_ends_at?: Date;
  target_type?: ApiFormElementWriteToFieldTargetType;
  field_id?: number | null;
  field_type?: ApiFieldTypes | null;
  table_id?: number | null;
  table_mode?: ApiFormElementWriteToFieldTableMode | null;
  show_current_row?: boolean;
  fill_in_all_columns?: boolean;
  column_ids_to_fill?: number[];
};

export const FormEditQuestionModal = ({
  element,
  onSave,
  onDelete,
  ...props
}: EditQuestionModalProps) => {
  const { close, openModal } = useModals();
  const { id } = { ...props };
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState(false);
  const [shouldUseEmoji, setShouldUseEmoji] = useState<boolean>(
    !!element.select_settings?.options?.at(0)?.emoji
  );

  const defaultDates = useMemo(() => {
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
    return { threeMonthsAgo, now: new Date() };
  }, []);

  const getValidationSchema = () => {
    switch (element.type) {
      case ApiFormElementType.employee_nps:
        return elementEmployeeNPSSettingsSchema;
      case ApiFormElementType.open_answer:
        return elementOpenAnswerSettingsSchema;
      case ApiFormElementType.file:
        return elementFileSettingsSchema;
      case ApiFormElementType.text:
        return elementTextSettingsSchema;
      case ApiFormElementType.select:
        return elementSelectSettingsSchema;
      case ApiFormElementType.scale:
        return elementScaleSettingsSchema;
      case ApiFormElementType.goals:
        return elementGoalsSettingsSchema;
      case ApiFormElementType.write_to_field:
        return elementWriteToFieldSettingsSchema;
      default:
        return z.object({
          title: z.string().min(1),
          description: z.string().nullable(),
        });
    }
  };

  const getDefaultValues = (): FormData => {
    const baseValues: FormData = {
      title: "",
      description: null,
    };

    switch (element.type) {
      case ApiFormElementType.employee_nps:
        return {
          ...element.employee_nps_settings,
          title: element.employee_nps_settings?.title || "",
          description: element.employee_nps_settings?.description || null,
          is_required: element.employee_nps_settings?.is_required || false,
          is_comment_enabled: element.employee_nps_settings?.is_comment_enabled || false,
        };
      case ApiFormElementType.open_answer:
        return {
          ...element.open_answer_settings,
          title: element.open_answer_settings?.title || "",
          description: element.open_answer_settings?.description || null,
          is_required: element.open_answer_settings?.is_required || false,
          is_comment_enabled: element.open_answer_settings?.is_comment_enabled || false,
        };
      case ApiFormElementType.file:
        return {
          ...element.file_settings,
          title: element.file_settings?.title || "",
          description: element.file_settings?.description || null,
          is_required: element.file_settings?.is_required || false,
          is_comment_enabled: element.file_settings?.is_comment_enabled || false,
        };
      case ApiFormElementType.text:
        return {
          ...baseValues,
          ...element.text_settings,
          title: element.text_settings?.title || "",
          description: element.text_settings?.description || null,
          text: element.text_settings?.text || null,
        };
      case ApiFormElementType.select:
        return {
          ...baseValues,
          ...element.select_settings,
          title: element.select_settings?.title || "",
          description: element.select_settings?.description || null,
          is_required: element.select_settings?.is_required || false,
          is_comment_enabled: element.select_settings?.is_comment_enabled || false,
          is_multiple: element.select_settings?.is_multiple || false,
          is_other: element.select_settings?.is_other || false,
          options: element.select_settings?.options || [],
        };
      case ApiFormElementType.scale:
        return {
          ...baseValues,
          ...element.scale_settings,
          title: element.scale_settings?.title || "",
          description: element.scale_settings?.description || null,
          is_required: element.scale_settings?.is_required || false,
          is_comment_enabled: element.scale_settings?.is_comment_enabled || false,
        };
      case ApiFormElementType.goals:
        return {
          ...baseValues,
          ...element.goals_settings,
          title: element.goals_settings?.title || "",
          description: element.goals_settings?.description || null,
          is_required: element.goals_settings?.is_required || false,
          is_comment_enabled: element.goals_settings?.is_comment_enabled || false,
          is_single_rating: element.goals_settings?.is_single_rating || false,
          show_when_no_goals: element.goals_settings?.show_when_no_goals || false,
          date_starts_at: element.goals_settings?.date_starts_at
            ? new Date(element.goals_settings?.date_starts_at)
            : defaultDates.threeMonthsAgo,
          date_ends_at: element.goals_settings?.date_ends_at
            ? new Date(element.goals_settings?.date_ends_at)
            : defaultDates.now,
        };
      case ApiFormElementType.write_to_field:
        return {
          ...baseValues,
          title: element.write_to_field_settings?.title || "",
          description: element.write_to_field_settings?.description || null,
          is_required: element.write_to_field_settings?.is_required ?? false,
          is_comment_enabled: element.write_to_field_settings?.is_comment_enabled ?? false,
          target_type:
            element.write_to_field_settings?.target_type ??
            ApiFormElementWriteToFieldTargetType.field,
          field_id: element.write_to_field_settings?.field_id ?? null,
          field_type: element.write_to_field_settings?.field_type ?? null,
          table_id: element.write_to_field_settings?.table_id ?? null,
          table_mode: element.write_to_field_settings?.table_mode ?? null,
          show_current_row: element.write_to_field_settings?.show_current_row ?? true,
          fill_in_all_columns: element.write_to_field_settings?.fill_in_all_columns ?? true,
          column_ids_to_fill: element.write_to_field_settings?.column_ids_to_fill ?? [],
        };
      default:
        return baseValues;
    }
  };

  const {
    control,
    handleSubmit,
    register,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(getValidationSchema()),
    values: getDefaultValues(),
    mode: "onChange",
  });

  const options = watch("options") || [];
  const watchedTargetType = watch("target_type");
  const watchedFieldId = watch("field_id");
  const watchedFieldType = watch("field_type");
  const watchedTableId = watch("table_id");
  const watchedTableMode = watch("table_mode");
  const watchedFillInAllColumns = watch("fill_in_all_columns");
  const watchedColumnIds = watch("column_ids_to_fill") || [];

  const isWriteToField = element.type === ApiFormElementType.write_to_field;
  const { data: appConfig } = useAppConfig();

  const employeeFields = useMemo(() => {
    if (!isWriteToField || !appConfig) return [];
    return (
      appConfig.ui.employee_profile_edit.tabs.flatMap((tabWrapper) =>
        tabWrapper.items.flatMap((item) => item.section?.fields ?? [])
      ) ?? []
    );
  }, [appConfig, isWriteToField]);
  const { data: allTables } = useAllTables(isWriteToField);
  const canShowCurrentRow =
    watchedTargetType === ApiFormElementWriteToFieldTargetType.table &&
    watchedTableMode === ApiFormElementWriteToFieldTableMode.create_new &&
    allTables?.some((table) => table.id === watchedTableId && table.is_historical);
  const { data: tableColumns } = useTableFields(
    watchedTableId ?? 0,
    isWriteToField &&
      watchedTargetType === ApiFormElementWriteToFieldTargetType.table &&
      watchedTableId != null
  );

  const { getFieldTitle, getTableTitle } = useCustomFieldsHook();
  const editableTableColumns = useMemo(
    () =>
      tableColumns?.filter(
        (column) =>
          column.type !== ApiFieldTypes.changed_by && column.type !== ApiFieldTypes.effective_to
      ),
    [tableColumns]
  );
  const tableColumnIds = useMemo(
    () => editableTableColumns?.map((column) => column.id) ?? [],
    [editableTableColumns]
  );
  const mandatoryTableColumnIds = useMemo(
    () =>
      editableTableColumns?.filter((column) => column.is_mandatory).map((column) => column.id) ??
      [],
    [editableTableColumns]
  );

  useEffect(() => {
    if (
      watchedTargetType !== ApiFormElementWriteToFieldTargetType.table ||
      watchedFillInAllColumns !== false ||
      tableColumnIds.length === 0
    ) {
      return;
    }

    const shouldSelectAllColumns = watchedColumnIds.length === 0;
    const selectedEditableColumnIds = watchedColumnIds.filter((columnId) =>
      tableColumnIds.includes(columnId)
    );
    const nextColumnIds = shouldSelectAllColumns
      ? tableColumnIds
      : Array.from(new Set([...selectedEditableColumnIds, ...mandatoryTableColumnIds]));

    if (
      nextColumnIds.length !== watchedColumnIds.length ||
      nextColumnIds.some((columnId) => !watchedColumnIds.includes(columnId))
    ) {
      setValue("column_ids_to_fill", nextColumnIds, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [
    mandatoryTableColumnIds,
    setValue,
    tableColumnIds,
    watchedColumnIds,
    watchedFillInAllColumns,
    watchedTargetType,
  ]);

  const combinedTargetOptions = useMemo<SelectOptionItem[]>(() => {
    const fieldOpts = (employeeFields ?? []).map((f: ApiField) => ({
      id: `field:${f.type}:${f.id}`,
      title: getFieldTitle(f),
      group: t("custom_fields:fields"),
    }));
    const tableOpts = (allTables ?? []).map((table: ApiTable) => ({
      id: `table:${table.id}`,
      title: getTableTitle(table),
      group: t("surveys:write_to_field_group_tables"),
    }));
    return [...fieldOpts, ...tableOpts];
  }, [employeeFields, allTables]);

  const currentTargetValue = useMemo(() => {
    if (
      watchedTargetType === ApiFormElementWriteToFieldTargetType.field &&
      watchedFieldId != null &&
      watchedFieldType != null
    ) {
      return `field:${watchedFieldType}:${watchedFieldId}`;
    }
    if (
      watchedTargetType === ApiFormElementWriteToFieldTargetType.table &&
      watchedTableId != null
    ) {
      return `table:${watchedTableId}`;
    }
    return undefined;
  }, [watchedTargetType, watchedFieldId, watchedFieldType, watchedTableId]);

  const selectedTargetName = useMemo(() => {
    if (!currentTargetValue) return null;
    const option = combinedTargetOptions.find((opt) => opt.id === currentTargetValue);
    return option && "title" in option ? option.title : null;
  }, [currentTargetValue, combinedTargetOptions]);

  const handleTargetChange = (value: SelectValueType) => {
    if (!value || typeof value !== "string") return;
    const [type, fieldTypeOrId, idStr] = value.split(":");
    const targetId = parseInt(idStr ?? fieldTypeOrId, 10);
    const validationOptions = { shouldValidate: true, shouldDirty: true };

    if (type === "field") {
      setValue("target_type", ApiFormElementWriteToFieldTargetType.field, validationOptions);
      setValue("field_id", targetId, validationOptions);
      setValue("field_type", fieldTypeOrId as ApiFieldTypes, validationOptions);
      setValue("table_id", null, validationOptions);
      setValue("table_mode", null, validationOptions);
      setValue("fill_in_all_columns", true, validationOptions);
      setValue("column_ids_to_fill", [], validationOptions);
    } else if (type === "table") {
      setValue("target_type", ApiFormElementWriteToFieldTargetType.table, validationOptions);
      setValue("table_mode", ApiFormElementWriteToFieldTableMode.create_new, validationOptions);
      setValue("table_id", targetId, validationOptions);
      setValue("field_id", null, validationOptions);
      setValue("field_type", null, validationOptions);
      setValue("fill_in_all_columns", true, validationOptions);
      setValue("column_ids_to_fill", [], validationOptions);
    }
  };

  const handleFillInAllColumnsChange = (fillInAllColumns: boolean) => {
    const validationOptions = { shouldValidate: true, shouldDirty: true };
    setValue("fill_in_all_columns", fillInAllColumns, validationOptions);
    setValue("column_ids_to_fill", fillInAllColumns ? [] : tableColumnIds, validationOptions);
  };

  const handleColumnToggle = (columnId: number, isSelected: boolean) => {
    if (mandatoryTableColumnIds.includes(columnId)) return;

    const currentIds = watchedColumnIds || [];
    if (isSelected) {
      setValue("column_ids_to_fill", [...currentIds, columnId]);
    } else {
      setValue(
        "column_ids_to_fill",
        currentIds.filter((id) => id !== columnId)
      );
    }
  };

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(options);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    const updatedItems = items.map((item, index) => ({
      ...item,
      sort: index,
    }));

    setValue("options", updatedItems);
  };

  const addOption = () => {
    openModal(FormEditScaleOrSelectOptionModal, {
      isSelect: true,
      item: {
        emoji: null,
        title: null,
        description: null,
        value: null,
      },
      onSave: async (item) => {
        setShouldUseEmoji(item.emoji !== null);
        setValue("options", [...options, item]);
      },
    });
  };

  const editOption = (index: number) => {
    const option = options[index];
    openModal(FormEditScaleOrSelectOptionModal, {
      isSelect: true,
      item: option,
      onSave: async (item) => {
        const updatedOptions = [...options];
        updatedOptions[index] = item;
        setValue("options", updatedOptions);
      },
    });
  };

  const addMultipleOptions = () => {
    openModal(OptionsBulkAddModal, {
      onAdd: (titles: string[]) => {
        const newOptions = titles.map((title) => ({
          title,
          description: null,
          emoji: shouldUseEmoji ? emojiDefaults.survey.default : null,
          value: null,
        }));
        setValue("options", [...options, ...newOptions], {
          shouldValidate: true,
          shouldDirty: true,
        });
      },
    });
  };

  const removeOption = (index: number) => {
    const updatedOptions = [...options];
    updatedOptions.splice(index, 1);

    const reindexedOptions = updatedOptions.map((item, idx) => ({
      ...item,
      sort: idx,
    }));

    setValue("options", reindexedOptions);
  };

  const onSubmit = handleSubmit(async (data) => {
    const updatedElement: ApiFormElementEdit = {
      ...element,
    };

    switch (element.type) {
      case ApiFormElementType.select:
        updatedElement.select_settings = {
          title: data.title,
          description: data.description,
          is_required: data.is_required || false,
          is_comment_enabled: data.is_comment_enabled || false,
          is_multiple: data.is_multiple || false,
          is_other: data.is_other || false,
          options: data.options || [],
        };
        break;
      case ApiFormElementType.employee_nps:
        updatedElement.employee_nps_settings = {
          title: data.title,
          description: data.description,
          is_required: data.is_required || false,
          is_comment_enabled: data.is_comment_enabled || false,
        };
        break;
      case ApiFormElementType.open_answer:
        updatedElement.open_answer_settings = {
          title: data.title,
          description: data.description,
          is_required: data.is_required || false,
          is_comment_enabled: data.is_comment_enabled || false,
        };
        break;
      case ApiFormElementType.file:
        updatedElement.file_settings = {
          title: data.title,
          description: data.description,
          is_required: data.is_required || false,
          is_comment_enabled: data.is_comment_enabled || false,
        };
        break;
      case ApiFormElementType.text:
        updatedElement.text_settings = {
          title: data.title,
          description: data.description,
          text: data.text || null,
        };
        break;
      case ApiFormElementType.scale:
        if (updatedElement.scale_settings) {
          updatedElement.scale_settings = {
            title: data.title,
            description: data.description,
            form_scale_id: updatedElement.scale_settings.form_scale_id,
            is_required: data.is_required || false,
            is_comment_enabled: data.is_comment_enabled || false,
          };
        }
        break;
      case ApiFormElementType.goals:
        if (updatedElement.goals_settings) {
          updatedElement.goals_settings = {
            title: data.title,
            description: data.description,
            form_scale_id: updatedElement.goals_settings.form_scale_id,
            is_required: data.is_required || false,
            is_comment_enabled: data.is_comment_enabled || false,
            is_single_rating: data.is_single_rating || false,
            show_when_no_goals: data.show_when_no_goals || false,
            date_starts_at: data.date_starts_at ?? defaultDates.threeMonthsAgo,
            date_ends_at: data.date_ends_at ?? defaultDates.now,
          };
        }
        break;
      case ApiFormElementType.write_to_field:
        updatedElement.write_to_field_settings = {
          title: data.title,
          description: data.description,
          is_required: data.is_required || false,
          is_comment_enabled: data.is_comment_enabled || false,
          target_type: data.target_type ?? ApiFormElementWriteToFieldTargetType.field,
          field_id:
            data.target_type === ApiFormElementWriteToFieldTargetType.field
              ? (data.field_id ?? null)
              : null,
          field_type:
            data.target_type === ApiFormElementWriteToFieldTargetType.field
              ? (data.field_type ?? null)
              : null,
          table_id:
            data.target_type === ApiFormElementWriteToFieldTargetType.table
              ? (data.table_id ?? null)
              : null,
          table_mode:
            data.target_type === ApiFormElementWriteToFieldTargetType.table
              ? (data.table_mode ?? null)
              : null,
          show_current_row: data.show_current_row ?? true,
          fill_in_all_columns:
            data.target_type === ApiFormElementWriteToFieldTargetType.table
              ? (data.fill_in_all_columns ?? true)
              : null,
          column_ids_to_fill:
            data.target_type === ApiFormElementWriteToFieldTargetType.table &&
            !data.fill_in_all_columns
              ? Array.from(
                  new Set([...(data.column_ids_to_fill ?? []), ...mandatoryTableColumnIds])
                )
              : null,
        };
        break;
    }

    setIsSaving(true);
    await onSave(updatedElement);
    setIsSaving(false);
    close(id);
  });

  const handleDelete = () => {
    openModal(ConfirmationModal, {
      question: t("common:attention"),
      text: t("common:delete_confirm"),
      acceptTitle: t("common:delete"),
      onAccept: async function () {
        await onDelete();
        close(id);
      },
    });
  };

  const tableRecordsBehavior = [
    {
      value: ApiFormElementWriteToFieldTableMode.create_new,
      titleKey: "surveys:write_to_field_mode_create_new",
      captionKey: "surveys:write_to_field_mode_create_new_caption",
    },
    {
      value: ApiFormElementWriteToFieldTableMode.create_multiple,
      titleKey: "surveys:write_to_field_mode_create_multiple",
      captionKey: "surveys:write_to_field_mode_create_multiple_caption",
    },
    // {
    //   value: ApiFormElementWriteToFieldTableMode.update_existing,
    //   titleKey: "surveys:write_to_field_mode_update_existing",
    //   captionKey: "surveys:write_to_field_mode_update_existing_caption",
    // },
  ];

  return (
    <>
      <Modal
        {...props}
        variant={"side"}
        title={t("surveys:edit_question")}
        layout={"stretch"}
        withCloser
        closeByEscEnabled
        actions={
          <Stack gap={"sm"}>
            <ServerErrorField errors={errors} />
            <Button
              size={"lg"}
              className={"w-full"}
              type={"submit"}
              onClick={onSubmit}
              isLoading={isSaving}
            >
              {t("common:save")}
            </Button>
          </Stack>
        }
      >
        <form onSubmit={onSubmit}>
          <Stack gap={"lg"}>
            <>
              <Input
                label={t("common:title")}
                type="text"
                {...register("title")}
                required={true}
                error={errors.title?.message}
              />
              <Stack>
                <Title header={t("common:description")} size={"xs"} paddingBottom={true} />
                <Controller
                  control={control}
                  name={"description"}
                  render={({ field: { value, onChange } }) => (
                    <RichEditor value={value ?? ""} onChange={onChange} />
                  )}
                />
              </Stack>
            </>

            {element.type === ApiFormElementType.goals && (
              <Stack>
                <Title
                  header={t("performance_review:goal_question_period_title")}
                  caption={t("performance_review:goal_question_period_caption")}
                  size={"xs"}
                />
                <Stack direction={"vertical"} gap={"md"}>
                  <Box className="flex-1">
                    <Controller
                      control={control}
                      name="date_starts_at"
                      render={({ field: { value, onChange } }) => (
                        <Datepicker
                          value={value}
                          onChange={onChange}
                          required
                          label={t("performance_review:goal_question_period_start")}
                          error={errors.date_starts_at?.message}
                        />
                      )}
                    />
                  </Box>

                  <Box className="flex-1">
                    <Controller
                      control={control}
                      name="date_ends_at"
                      render={({ field: { value, onChange } }) => (
                        <Datepicker
                          value={value}
                          onChange={onChange}
                          required
                          label={t("performance_review:goal_question_period_end")}
                          error={errors.date_ends_at?.message}
                        />
                      )}
                    />
                  </Box>
                </Stack>
              </Stack>
            )}

            {element.type === ApiFormElementType.text && (
              <Stack>
                <Title
                  header={t("surveys:question_type_text_full")}
                  size={"xs"}
                  paddingBottom={true}
                  paddingTop={false}
                />
                <Controller
                  control={control}
                  name={"text"}
                  render={({ field: { value, onChange } }) => (
                    <RichEditor value={value ?? ""} onChange={onChange} />
                  )}
                />
              </Stack>
            )}

            <Stack gap={"sm"}>
              {[
                ApiFormElementType.open_answer,
                ApiFormElementType.file,
                ApiFormElementType.employee_nps,
                ApiFormElementType.select,
                ApiFormElementType.scale,
                ApiFormElementType.goals,
                ApiFormElementType.write_to_field,
              ].includes(element.type) && (
                <>
                  <Title
                    header={t("common:settings")}
                    size={"xs"}
                    paddingBottom={true}
                    paddingTop={false}
                  />
                  <Controller
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <Switch
                        checked={value}
                        onChange={onChange}
                        title={t("surveys:question_mandatory")}
                      />
                    )}
                    name={"is_required"}
                  />
                  <Controller
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <Switch
                        checked={value}
                        onChange={onChange}
                        title={t("surveys:question_comment_enabled")}
                      />
                    )}
                    name={"is_comment_enabled"}
                  />
                </>
              )}

              {element.type === ApiFormElementType.goals && (
                <>
                  <Controller
                    control={control}
                    name={"is_single_rating"}
                    render={({ field: { value, onChange } }) => (
                      <Switch
                        checked={value}
                        onChange={onChange}
                        title={t("performance_review:goal_question_single_rating")}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name={"show_when_no_goals"}
                    render={({ field: { value, onChange } }) => (
                      <Switch
                        checked={value}
                        onChange={onChange}
                        title={t("performance_review:goal_question_show_when_no_goals")}
                      />
                    )}
                  />
                </>
              )}

              {element.type === ApiFormElementType.select && (
                <>
                  <Controller
                    name="is_multiple"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <Switch
                        checked={value}
                        onChange={onChange}
                        title={t("surveys:allow_multiple_selection")}
                      />
                    )}
                  />

                  {/*<Controller*/}
                  {/*  name="is_other"*/}
                  {/*  control={control}*/}
                  {/*  render={({ field: { value, onChange } }) => (*/}
                  {/*    <Switch*/}
                  {/*      checked={value}*/}
                  {/*      onChange={onChange}*/}
                  {/*      title={t("surveys:allow_other_option")}*/}
                  {/*    />*/}
                  {/*  )}*/}
                  {/*/>*/}
                </>
              )}
            </Stack>
            {element.type === ApiFormElementType.select && (
              <>
                <Stack gap={"sm"} className={"mt-4"}>
                  <Title
                    header={t("common:options")}
                    size={"xs"}
                    paddingTop={false}
                    paddingBottom={true}
                  />

                  <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="options">
                      {(provided) => (
                        <div
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                          className="flex flex-col -m-1"
                        >
                          {options.map((option, index) => (
                            <Draggable
                              key={`option-${index}`}
                              draggableId={`option-${index}`}
                              index={index}
                            >
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  className="group/option rounded-xs px-4 py-2 bg-dark/5 border border-dark/5 my-1"
                                >
                                  <Box>
                                    <Stack
                                      justify={"between"}
                                      direction={"horizontal"}
                                      items={"center"}
                                    >
                                      <Stack direction={"horizontal"} items={"center"} gap={"xs"}>
                                        {option.emoji && (
                                          <Avatar emoji={option.emoji} variant={"plain"} />
                                        )}
                                        {option.title && (
                                          <Text className={"text-title"}>{option.title}</Text>
                                        )}
                                      </Stack>
                                      <Stack
                                        direction={"horizontal"}
                                        gap={"sm"}
                                        items={"center"}
                                        className={
                                          snapshot.isDragging
                                            ? "opacity-100"
                                            : "opacity-0 pointer-events-none transition-opacity group-hover/option:opacity-100 group-hover/option:pointer-events-auto focus-within:opacity-100 focus-within:pointer-events-auto"
                                        }
                                      >
                                        <ButtonIcon
                                          variant={"plain"}
                                          icon={<FontAwesomeIcon icon={"fa-light fa-trash"} />}
                                          onClick={() => {
                                            removeOption(index);
                                          }}
                                        />
                                        <ButtonIcon
                                          variant={"plain"}
                                          icon={
                                            <FontAwesomeIcon icon={"fa-light fa-pen-to-square"} />
                                          }
                                          onClick={() => {
                                            editOption(index);
                                          }}
                                        />
                                        <Box {...provided.dragHandleProps}>
                                          <FontAwesomeIcon
                                            className={"m-2"}
                                            icon="fa-light fa-grip-dots-vertical"
                                          />
                                        </Box>
                                      </Stack>
                                    </Stack>
                                  </Box>
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>

                  <Stack direction="horizontal" gap="sm" justify="between">
                    <Button
                      variant="plain"
                      className={"text-center w-1/2"}
                      onClick={addOption}
                      leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}
                    >
                      {t("common:add_option")}
                    </Button>
                    <Button
                      variant="plain"
                      className={"text-center w-1/2"}
                      onClick={addMultipleOptions}
                      leftIcon={
                        <FontAwesomeIcon icon="fa-light fa-rectangle-history-circle-plus" />
                      }
                    >
                      {t("common:add_multiple")}
                    </Button>
                  </Stack>
                </Stack>

                {errors.options && (
                  <div className="text-danger pt-2 pb-4">{t("common:options_at_least_one")}</div>
                )}
              </>
            )}

            {element.type === ApiFormElementType.write_to_field && (
              <Stack>
                <Title
                  header={t("surveys:write_to_field_destination_title")}
                  paddingBottom
                  paddingTop
                  size={"xs"}
                />
                <Select
                  emptyTitle={t("surveys:write_to_field_destination_placeholder")}
                  options={combinedTargetOptions}
                  value={currentTargetValue}
                  required
                  onChange={(value) => {
                    handleTargetChange(value);
                  }}
                  error={
                    errors.field_id?.message ||
                    errors.field_type?.message ||
                    errors.table_id?.message
                  }
                  searchable
                />
                {selectedTargetName && (
                  <Text className={"py-2 text-sm text-secondary"}>
                    {t("custom_fields:settings_title")} → {selectedTargetName}
                  </Text>
                )}
                {watchedTargetType === ApiFormElementWriteToFieldTargetType.table &&
                  watchedTableId != null && (
                    <>
                      <Stack gap={"sm"}>
                        <Title
                          header={t("surveys:write_to_field_table_mode_title")}
                          size={"xs"}
                          paddingTop
                        />
                        <Controller
                          control={control}
                          name={"table_mode"}
                          render={({ field: { value, onChange } }) => (
                            <Stack
                              className={"px-4 py-2 bg-dark/5 border border-dark/5 rounded-xs"}
                            >
                              {tableRecordsBehavior.map((option) => (
                                <Radio
                                  key={option.value}
                                  value={option.value}
                                  checked={value === option.value}
                                  onChange={() => onChange(option.value)}
                                  title={t(option.titleKey)}
                                  caption={t(option.captionKey)}
                                />
                              ))}
                            </Stack>
                          )}
                        />
                      </Stack>
                      {canShowCurrentRow && (
                        <Stack gap={"sm"}>
                          <Title
                            header={t("surveys:write_to_field_show_current_row_title")}
                            size={"xs"}
                            paddingTop
                          />
                          <Controller
                            control={control}
                            name="show_current_row"
                            render={({ field: { value, onChange } }) => (
                              <Switch
                                size="sm"
                                checked={value ?? true}
                                onChange={onChange}
                                title={t("surveys:write_to_field_show_current_row")}
                              />
                            )}
                          />
                        </Stack>
                      )}
                      <Stack gap={"sm"}>
                        <Title
                          header={t("surveys:write_to_field_columns_title")}
                          size={"xs"}
                          paddingTop
                        />
                        <Controller
                          control={control}
                          name={"fill_in_all_columns"}
                          render={({ field: { value } }) => (
                            <Switch
                              size={"sm"}
                              checked={value ?? true}
                              onChange={(checked) => {
                                handleFillInAllColumnsChange(checked);
                              }}
                              title={t("surveys:write_to_field_all_columns")}
                              description={
                                editableTableColumns
                                  ? t("plurals:fields_selected", {
                                      count:
                                        (value ?? true)
                                          ? editableTableColumns.length
                                          : watchedColumnIds.length,
                                    })
                                  : undefined
                              }
                            />
                          )}
                        />
                        {!(watchedFillInAllColumns ?? true) &&
                          editableTableColumns &&
                          editableTableColumns.length > 0 && (
                            <Stack className={"pl-9 pt-2"}>
                              {editableTableColumns.map((column: ApiField) => {
                                const isMandatoryColumn = column.is_mandatory;
                                const isSelected =
                                  watchedColumnIds.includes(column.id) || isMandatoryColumn;

                                return (
                                  <Checkbox
                                    key={column.id}
                                    checked={isSelected}
                                    readOnly={isMandatoryColumn}
                                    onChange={() => handleColumnToggle(column.id, !isSelected)}
                                    title={getFieldTitle(column)}
                                  />
                                );
                              })}
                            </Stack>
                          )}
                      </Stack>
                    </>
                  )}
              </Stack>
            )}

            <DeleteListItem
              title={t("surveys:delete_from_item_question")}
              onDelete={handleDelete}
            />
          </Stack>
        </form>
      </Modal>
    </>
  );
};
